import { observer } from "mobx-react-lite";
import styled from "styled-components";

import { ExternalLink, Tag } from "@components";

import { SectionHeader } from "@pages/editor/components/SectionHeader";
import { SectionList } from "@pages/editor/components/SectionList";
import { SuccessResult } from "@pages/editor/stores";

import { LineNumber } from "./LineNumber";

const LineNumberSerial = styled(LineNumber)`
  :not(:first-child)::before {
    content: ", ";
  }
`;

interface Props {
  result: SuccessResult;
}

const TestResultTag = styled(Tag)`
  font-weight: var(--global-font-weight-bold);
  min-width: 46px; /* wide enough for PASS and FAIL */
  text-align: center;
  text-transform: uppercase;
`;

const LinkContainer = styled.p`
  display: flex;
  justify-content: center;
  font-size: var(--global-font-size-xs);
`;

const PASS_TAG = <TestResultTag intent="success">pass</TestResultTag>;
const FAIL_TAG = <TestResultTag intent="danger">fail</TestResultTag>;

const TestCase = styled.li`
  display: flex;
  align-items: center;
  gap: var(--global-spacing-m);
`;

const Code = styled.span`
  font-family: var(--global-font-family-monospace);
`;

const Tests = ({ result }: Props) => {
  const { bundle } = result;
  const { expectedMatches, expectedNotMatches } = bundle;
  const actualMatches = result.response.semgrep_result.output.results.map(
    (match) => match.start.line
  );
  const extraneousMatches = new Set(
    actualMatches.filter(
      (lineno) =>
        expectedMatches.indexOf(lineno) === -1 &&
        expectedNotMatches.indexOf(lineno) === -1
    )
  );

  if (expectedMatches.length === 0 && expectedNotMatches.length === 0)
    return (
      <>
        <SectionHeader>No test assertions</SectionHeader>
        <SectionList>
          <p style={{ marginBottom: "4px" }}>
            Add <Code>ruleid: {"<rule-id>"}</Code> above lines your rule should
            catch to signify a test.
          </p>
          <LinkContainer>
            <ExternalLink
              stayVisible={true}
              href="https://semgrep.dev/docs/writing-rules/testing-rules/"
            >
              More info on how to add test assertions
            </ExternalLink>
          </LinkContainer>
        </SectionList>
      </>
    );

  return (
    <>
      <SectionHeader>Test cases</SectionHeader>
      <SectionList>
        {expectedMatches.map((lineno) => (
          <TestCase>
            {actualMatches.includes(lineno) ? PASS_TAG : FAIL_TAG}
            <div>
              <LineNumber line={lineno} /> must match
            </div>
          </TestCase>
        ))}
        {expectedNotMatches.map((lineno) => (
          <TestCase>
            {!actualMatches.includes(lineno) ? PASS_TAG : FAIL_TAG}
            <div>
              <LineNumber line={lineno} /> must not match
            </div>
          </TestCase>
        ))}
        <TestCase>
          {extraneousMatches.size === 0 ? PASS_TAG : FAIL_TAG}
          <div>
            Other lines must not match.
            {extraneousMatches.size === 0 ? (
              " None match."
            ) : (
              <>
                {" "}
                Unexpected {extraneousMatches.size === 1
                  ? "match"
                  : "matches"}{" "}
                on{" "}
                {[...extraneousMatches].map((lineno) => (
                  <LineNumberSerial line={lineno} />
                ))}
              </>
            )}
          </div>
        </TestCase>
      </SectionList>
    </>
  );
};

export const TestsSection = observer(Tests);
